import React, { forwardRef,useImperativeHandle, useRef, useState } from "react";
import {  RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCRadio, RCCRefModalProps, RCCServicesApi, RCCSwitch, RCCTextArea, RCCTooltip } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import ModalSide from "src/components/Modal/styles";
import LovUsers from "src/components/Lov/Users";
import cardStyle from "src/components/Checklists/styles";
import Loader from "src/components/Loader";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import { addDays } from "date-fns";
import {  IComponentRisksPanelModal, IRisks } from "../interface";
import { registerOccurenceSchema } from "../schemas";

export interface IRefModalRegisterIncident{
    actionButtonRegisterIncident: (row: IRisks) => void;
}
const ModalRgisterIncident: React.ForwardRefRenderFunction<IRefModalRegisterIncident,IComponentRisksPanelModal> = ({
	onCallbackUpdate
}, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const [riskData, setRiskData] = useState<IRisks>({} as IRisks);
	const [contigencyPlans, setContigencyPlans] = useState<any[]>();
	const [showContigencyPlans, setShowContigencyPlans] = useState(false);
	const modalRegisterIncident = useRef<RCCRefModalProps>(null);
	const [loadingActions,setLoadingActions] = useState(false);
	const [selectedContigency,setSelectedContigency] = useState(false);

	useImperativeHandle(ref, () => ({
		actionButtonRegisterIncident
	}));

	const { handleSubmit, 
		register, 
		reset, 
		setError, 
		control,
		formState: { errors }, 
		clearErrors
	} = useForm();

	const { 
		fields, 
		remove,
		append 
	} = useFieldArray({
		control,
		name: "actions"
	});

	const actionButtonRegisterIncident = (row: IRisks) => {
		setSelectedContigency(false);
		setLoadingActions(false);
		setShowContigencyPlans(false);
		setLoading(true);
		reset();
		setRiskData(row); 	
		listContigencyPlans(row);
	};

	const listContigencyPlans = async (row: IRisks) => {
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Risks/ListContigencyPlans",
				urlParameters: {
					riscoId: row.rib001Id
				}
			}); 

			setContigencyPlans(data);

			setLoading(false);
			modalRegisterIncident.current?.openModal();

		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar o risco"), addToast);
			setLoading(false);
		}
	};

	const onSubmit =  async (data: any) => {

		try{
			clearErrors();
			modalRegisterIncident.current?.setLoadingButton();

			const objToInt = (obj: any) => {
				if(typeof obj === "object")
					return parseInt(obj.value, 10);
				if(typeof obj === "string")
					return parseInt(obj, 10);
				if(typeof obj !== "number")
					return null;
				return obj;
			};

			const dataChanged = data;

			if(dataChanged.actions)
				dataChanged.actions.forEach((action : any) => {
					const actionChange = action;
					actionChange.rib006Efa005Id = objToInt(action.rib006Efa005Id);
				});

			await registerOccurenceSchema(translate).validate(data,{
				abortEarly: false
			});

			await RCCServicesApi.postRequest({
				method: "Risks/RegisterIncident",
				body: {
					...data,
					rib001Id: riskData.rib001Id
				}
			});

			addToast({
				color: "green",
				description:  translate("Incidência registrada com sucesso"),
				title: translate("Sucesso")
			});

			if(onCallbackUpdate){
				onCallbackUpdate(riskData.rib001Id);
			}

			modalRegisterIncident.current?.resetLoadingButton();
			modalRegisterIncident.current?.closeModal();
		}catch(err){
			modalRegisterIncident.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;
			standardErrorMessages(err, translate("Erro ao registrar incidência"), addToast);
		}
	};


	const getActions = async (value : any) => {
		try{
			setSelectedContigency(true);
			setLoadingActions(true);
			remove();
			const { data } = await RCCServicesApi.getRequest({
				method: "Risks/ListContingencyPlanActions",
				urlParameters: {
					planContId: value
				}
			});

			data.forEach((element: any) => {
				const changedElement = element;
				changedElement.obrigatory = true;
				append({...element});
			});
			setLoadingActions(false);
		}catch(err){
			modalRegisterIncident.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;
			standardErrorMessages(err, translate("Erro ao carregar ações"), addToast);
			setLoadingActions(false);
		}
	};

	return (
		<>
			<Modal
				ref={modalRegisterIncident}	
				header={{
					title: translate("Registro de incidência"),
					icon: {
						name: "checkCircle"
					},
					subtitle: `${translate("Risco")}: ${riskData.rib001Titulo}`
				}}
				onSubmit={handleSubmit(onSubmit)}
				styleInline={ModalSide}
			>

				<RCCGrid
					item
					container
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						direction="column"
						alignItems="center"
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={12}
						>
							<RCCTextArea 
								name="rib004Motivo"
								label={translate("Motivo")} 
								maxLength={400}
								register={register}
								styleInline=">textarea{ max-height: 400px !important;} "
								error={errors.rib004Motivo}
								required
							/>
						</RCCGrid>
					</RCCGrid>
					{contigencyPlans && contigencyPlans.length > 0 && (
						<RCCGrid
							container
							item
						>
							<RCCSwitch 
								name="contigency"
								label={translate("Disparar plano de contingência")} 
								defaultChecked={false}
								onClick={() => {
									remove();
									setShowContigencyPlans(!showContigencyPlans);
									setSelectedContigency(false);
								}}
							/>
						</RCCGrid>
					)}
					
					<RCCGrid
						container
						item
						direction="column"
						alignItems="center"
						style={{ marginTop: "20px" }}
					>

						{showContigencyPlans && contigencyPlans && contigencyPlans.length > 0 && (
							<RCCGrid
								container
								item
								xs={12}
								sm={12}
							>
								<RCCRadio
									name="contigencyPlanId"
									label={`${translate("Selecione um plano de contingência")}:`}
									options={contigencyPlans.map((e) => ({
										value: e.rib005Id.toString(),
										label: e.rib005Titulo
									}))}
									onChange={(e) => getActions(e.target.value)}				
									register={register}
									direction="vertical"
									color="purple"
								/>
							</RCCGrid>
						)}
						
						{contigencyPlans && contigencyPlans.length <= 0 && (
							<RCCAlert 
								title={translate("Atenção")}	
								description={translate("Esta incidência não possui nenhum plano de contigência")}
							/>
						)}

						{(showContigencyPlans && selectedContigency) && (
							<RCCGrid
								container
								item
								xs={12}
								sm={12}
								justifyContent="flex-end"
							>
								<RCCTooltip 
									text={translate("Adicionar ação")}
									color="purple"
								>
									<RCCButton 
										color="purple" 
										icon={{name:"plus"}}
										size="sm"
										onClick={() => append({})}
										layout="isRounded" 
									/>
								</RCCTooltip>
							</RCCGrid>
						)}		

						{loadingActions && (
							<Loader />
						)}

						{!loadingActions && fields.map((action: any, index: number) => (
							<RCCGrid 
								item
								container
								xs={12}
								md={12}
								alignItems="center"
								justifyContent="space-between"
								key={action.id}
								styleInline={cardStyle}
							>
								<RCCGrid 
									item
									container
									xs={12}
									alignItems="center"
									justifyContent="center"
								>
									<RCCGrid 
										item
										container
										xs={11}
									>
										<RCCInput 
											name={`actions[${index}].rib006Titulo`}
											label={translate("Título")}
											maxLength={60}
											register={register}
											defaultValue={action.rib006Titulo ?? ""}
											error={errors?.actions && errors?.actions[index]?.rib006Titulo}
											required
										/>
									</RCCGrid>
	
									<RCCGrid 
										item
										container
										xs={1}
										justifyContent="center"
									>
										<RCCTooltip
											color="red"
											text={translate("Remover ação")}
											style={{ marginTop: "15px" }}
										>
											<RCCButton
												color="red"
												icon={{
													name: "trash"
												}}
												size="sm"
												layout="isRounded"
												onClick={() => remove(index)}
												disabled={action.obrigatory ?? false}
											/>
										</RCCTooltip>
									</RCCGrid>						
								</RCCGrid>
								<RCCGrid 
									item
									container
									xs={12}
									alignItems="center"
									justifyContent="center"
								>
									<LovUsers 
										control={control}
										name={`actions[${index}].rib006Efa005Id`}
										label={translate("Responsável")}
										defaultValue={action.rib006Titulo ? {
											value: action.rib006Efa005Id.toString(),
											label: action.rib006Efa005Nome
										} : ""}
										error={errors?.actions && errors?.actions[index]?.rib006Efa005Id}
										required
									/>
								</RCCGrid>
	
								<RCCGrid 
									item
									container
									xs={12}
									alignItems="center"
									justifyContent="center"
								>
									<RCCTextArea 
										label={`${translate("Como")} ?`}
										name={`actions[${index}].rib006Como`}
										maxLength={4000}	
										register={register}
										defaultValue={action.rib006Como ?? ""}
										error={errors?.actions && errors?.actions[index]?.rib006Como}
										required
									/>
								</RCCGrid>
	
								<RCCGrid 
									item
									container
									xs={12}
									alignItems="center"
									justifyContent="center"
								>
									<DateTimePickerRange
										control={control}
										label={`${translate("Data prevista")}`}
										labelSeparator={`${translate("até")}`}
										nameEnd={`actions[${index}].expectedEndDate`}
										nameInit={`actions[${index}].expectedInitialDate`}
										defaultValueInit={new Date()}
										defaultValueEnd={addDays(new Date(), action.rib006Dias)}
										type="datePicker"
										error={errors?.actions && (errors.actions[index]?.expectedInitialDate||errors.actions[index]?.expectedEndDate)}
										required
									/>
								</RCCGrid>	
							</RCCGrid>	
						))}				

					</RCCGrid>

				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(ModalRgisterIncident);
